import { Injectable, ElementRef } from '@angular/core';
import interact from 'interactjs';

/**
 * This class defines touch interaction of components. It can be added to all UI components that need this functionality.
 *
 */
@Injectable()
export class InteractionService {
  constructor() {
  }

  /**
   * Adds a pinch-interaction to the elements with targetClass that changes their size.
   *
   * @param {string} targetClass
   * @param {(event: any) => void} onStart
   * @param {(size: {newWidth: number; newHeight: number}, event: any) => void} onMove
   * @param {(event: any) => void} onEnd
   * @param whRatio
   */
  addPinchToResizeInteraction(
    targetClass: string,
    onStart?: (event: any) => void,
    onMove?: (size: { newWidth: number; newHeight: number }, event: any) => void,
    onEnd?: (event: any) => void,
    whRatio?: number) {

    interact(targetClass).gesturable({
      onstart: (event: any) => {
        if (onStart) {
          onStart(event);
        }
      },
      onmove: function (event: any) {
        const target = event.target;
        const resizeFactor = 1 + (0.1 * event.ds);

        const size = {
          newWidth: whRatio ? (target.offsetHeight) * resizeFactor * whRatio : (target.offsetWidth) * resizeFactor,
          newHeight: (target.offsetHeight) * resizeFactor
        };
        if (onMove) {
          onMove(size, event);
        }
        target.style.width = `${size.newWidth}px`;
        target.style.height = `${size.newHeight}px`;
      },
      onend: function (event: any) {
        if (onEnd) {
          onEnd(event);
        }
      }
    });
  }

  /**
   * Puts the target back into the state it had before the PinchToResize-Interaction
   */
  undoPinchToResizeInteraction(target: HTMLElement) {
    // TODO Please verify this implementation. PinchToResize doesnt set 'style' or 'data-scale'
    target.removeAttribute('style');
    target.removeAttribute('data-scale');
  }

  /**
   * Adds a pinch-interaction to the elements with targetClass that changes their size.
   *
   * @param targetClass
   * @param onStart
   * @param onMove
   * @param onEnd
   */
  addPinchToZoomInteraction(
    targetClass: string,
    onStart?: (event: any) => any,
    onMove?: (size: { scale: number }, event: any) => any,
    onEnd?: (event: any) => any) {

    interact(targetClass).gesturable({
      onstart: (event: any) => {
        if (onStart) {
          onStart(event);
        }
      },
      onmove: function (event: any) {
        const target = event.target;
        // keep the dragged position in the data-x/data-y attributes
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1) + event.ds * 0.5;
        const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
        const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

        const size = { scale: scale };
        if (onMove) {
          onMove(size, event);
        }
        // translate the element
        target.style.webkitTransform = `matrix(${size.scale}, 0, 0, ${size.scale}, ${x}, ${y})`;
        target.style.transform = `matrix(${size.scale}, 0, 0, ${size.scale}, ${x}, ${y})`;
        event.target.setAttribute('data-scale', size.scale);
      },
      onend: function (event) {
        if (onEnd) {
          onEnd(event);
        }
      }
    });
  }

  /**
   * Puts the target back into the state it had before the PinchToZoom
   */
  undoPinchToZoomInteraction(target: HTMLElement) {
    const x = (parseFloat(target.getAttribute('data-x')) || 0);
    const y = (parseFloat(target.getAttribute('data-y')) || 0);
    target.style.transform = `matrix(1, 0, 0, 1, ${x}, ${y})`;
    target.removeAttribute('data-scale');
  }

  addClickInteractionElement(elementRef: ElementRef<any>, onClickDown?: (event: any) => any) {
    interact(elementRef.nativeElement).on('tap', (event) => {
      if (onClickDown) {
        onClickDown(event);
      }
    });
  }

  /**
   * this add base dragging functionality to the specified components that belong to targetClass
   * @param {string} targetClass the class of the components to add drag functionality to
   * @param onStart
   * @param onMove
   * @param onEnd
   * @param manualStart
   *
   */
  addDragInteraction(targetClass: string,
    onStart?: (event: any) => any,
    onMove?: (position: { x: number; y: number }, event: any) => any,
    onEnd?: (event: any) => any,
    manualStart?: boolean) {

    interact(targetClass).draggable({
      inertia: false,
      autoScroll: false,
      manualStart: manualStart,

      onstart: event => {
        if (onStart) {
          onStart(event);
        }
      },
      onmove: function (event: any) {
        const target: HTMLElement = event.target;

        // keep the dragged position in the data-x/data-y attributes
        const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
        const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1);

        const position = { x: x, y: y };
        if (onMove) {
          onMove(position, event);
        }

        // translate the element
        target.style.webkitTransform = `matrix(${scale}, 0, 0, ${scale}, ${position.x}, ${position.y})`;
        target.style.transform = `matrix(${scale}, 0, 0, ${scale}, ${position.x}, ${position.y})`;
        // update the position attributes
        target.setAttribute('data-x', `${position.x}`);
        target.setAttribute('data-y', `${position.y}`);
      },
      onend: function (event) {
        if (onEnd) {
          onEnd(event);
        }
      }
    });
  }

  /**
   * Puts the target back into the state it had before the PinchToResize-Interaction
   */
  undoDragInteraction(target: HTMLElement) {
    target.removeAttribute('style');
    target.removeAttribute('data-x');
    target.removeAttribute('data-y');
  }

  /**
   * This function defines double tap for components.
   * @param {string} targetClass
   * @param {(event: any) => void} onDoubleTap
   */
  setUpDoubleTap(targetClass: string, onDoubleTap?: (event: any) => void) {
    interact(targetClass).on('doubletap', (event) => {
      if (onDoubleTap) {
        onDoubleTap(event);
      }
    });
  }

  /**
   *
   * @param draggableElement the HTMLElement that will be made draggable, i.e. that will react to the drag gesture
   * @param dragTargetElement  the HTMLElement that will be moved when draggableElement is dragged
   * @param afterMoveCallback A callback function that will be executed directly after the onmove-code
   */
  setUpDraggable(draggableElement: HTMLElement, dragTargetElement: HTMLElement, afterMoveCallback?: (event: any) => void) {
    interact(draggableElement).draggable({
      onmove: function (event) {
        const x = Number(dragTargetElement.getAttribute('data-x') || 0) + event.dx;
        const y = Number(dragTargetElement.getAttribute('data-y') || 0) + event.dy;
        // translate the element
        dragTargetElement.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
        // update the posiion attributes
        dragTargetElement.setAttribute('data-x', x);
        dragTargetElement.setAttribute('data-y', y);

        if (afterMoveCallback) {
          afterMoveCallback(event);
        }
      }
    });
  }


  /**
   * Removes all interactjs-listeners from elements with the given selector
   */
  unset(targetSelector: string): void {
    interact(targetSelector).unset();
  }

  addResizeInteraction(target) {
    interact(target)
      .resizable({
        edges: {
          top: false,
          left: false,
          bottom: '.widget__resizer',
          right: '.widget__resizer'
        },
        listeners: {
          move(event) {
            const percentViewportWidth = event.rect.width / document.documentElement.clientWidth * 100;
            const percentViewportHeight = event.rect.height / document.documentElement.clientHeight * 100;
            event.target.style.width = percentViewportWidth + 'vw';
            event.target.style.height = percentViewportHeight + 'vh';
          }
        },
      });
  }
}
